import Skeleton from 'react-loading-skeleton';

import SavingsIcon from '~/shared/assets/icons/savings.svg';
import CloudIcon from '~/shared/assets/icons/cloud.svg';
import Progress from '~/shared/components/Progress';
import Legend from '~/shared/components/Legend';

type Props = {
  title: string;
  savingsText: string;
  potentialText: string;
  percentage: number;
  savingLegendText: string;
  potentialLegendText: string;
  loading?: boolean;
  variant: 'purple' | 'green';
};

export default function SavingsSection({
  title,
  savingLegendText,
  savingsText,
  potentialLegendText,
  potentialText,
  percentage,
  loading,
  variant,
}: Props) {
  const { hexColor, Icon, progressDropShadow, progressInnerShadow, gradient } =
    variantsMap[variant];

  return (
    <div className="w-[49.5%] flex flex-col gap-3 items-start">
      <div className={`p-1.5 rounded-[12.5px]`} style={{ backgroundColor: `${hexColor}10` }}>
        <Icon className="w-[34px] h-[34px]" style={{ fill: hexColor }} />
      </div>
      <h3 className="font-medium mt-2">{title}</h3>
      {loading ? (
        <Skeleton
          containerClassName="h-[24px] block"
          className="h-[24px] leading-[unset!important] rounded-[8px]"
          baseColor="#E2E6EC"
          highlightColor="#FFFF"
          width={115}
        />
      ) : (
        <h4 className="font-semibold text-2xl leading-[24px]">
          {savingsText}
          <span className="text-gray font-medium text-xs text-[#949EAC] ml-1">
            / {potentialText}
          </span>
        </h4>
      )}
      <div className="mt-8 w-full">
        <Progress
          gradient={gradient}
          percentage={percentage}
          dropShadow={progressDropShadow}
          innerShadow={progressInnerShadow}
          loading={loading}
        />
      </div>
      <div className="flex justify-between gap-4">
        <Legend hexColor={hexColor} title={savingLegendText} />
        <Legend hexColor="#949EAC" title={potentialLegendText} />
      </div>
    </div>
  );
}

const variantsMap: {
  [k in Props['variant']]: {
    hexColor: string;
    progressDropShadow: string;
    progressInnerShadow: string;
    Icon: typeof SavingsIcon;
    gradient: string;
  };
} = {
  purple: {
    hexColor: '#5403D7',
    progressDropShadow: '0 4px 64px rgba(49,197,137,0.2)',
    progressInnerShadow: 'inset 0 4px 34px rgba(49,197,137,0.3)',
    Icon: SavingsIcon,
    gradient: 'linear-gradient(270deg, #5403D7 0%, #5403D7 0.01%, #D1B4FF 100%, #D1B4FF 100%)',
  },
  green: {
    hexColor: '#11F1C6',
    progressDropShadow: '0 4px 64px rgba(118,221,197,0.25)',
    progressInnerShadow: 'inset 0 4px 34px rgba(118,221,197, 0.3)',
    Icon: CloudIcon,
    gradient: 'linear-gradient(270deg, #11F1C6 0%, #a6e8e8 100%)',
  },
};
